import * as React from 'react';
import '../../Css/Global/Global.css';

import {Grid} from "@mui/material";

import BalanceIcon from '@mui/icons-material/Balance';

import TableIcmsEstruturaMercadologica from './TableIcmsEstruturaMercadologica';
import DonutChartWithTable from '../../Components/DonutChartWithTable';
import SecaoDebitosIcmsDrillDownModal from '../../Components/SecaoDebitosIcmsDrillDown/SecaoDebitosIcmsDrillDownModal';
import { useSecaoDebitosIcmsDrillDown } from '../../Hooks/useSecaoDebitosIcmsDrillDown';

import { DebitoIcmsEstruturaMercadologicaSecaoGrafico, Secao, SecaoICMS } from './types';
import SpedStatusHandler from '../Dashboard/SpedStatusHandler';
import { formatHorizontalBarLabel } from '../../Components/DrillDown/formatters';
import InfoCard from '../Dashboard/InfoCard';
  

const AnalisesEstruturaMercadologica: React.FC = () => {

    const [loading, setLoading] = React.useState(true);

    const [spedFiscalIdSelecionado, setSpedFiscalIdSelecionado] = React.useState<number | null>(null);

    // Hook para drilldown de seções
    const {
        isModalOpen: isSecaoModalOpen,
        drillDownData: secaoDrillDownData,
        loading: secaoDrillDownLoading,
        selectedSecao,
        error: secaoDrillDownError,
        openModal: openSecaoModal,
        closeModal: closeSecaoModal,
        retry: retrySecaoModal
    } = useSecaoDebitosIcmsDrillDown();

    const [dadosEstruturaMercadologicaIcms, setDadosEstruturaMercadologicaIcms] = React.useState<Secao[]>([]);
    const [dadosEstruturaMercadologicaIcmsSt, setDadosEstruturaMercadologicaIcmsSt] = React.useState<Secao[]>([]);
    
    const [dadosDebitoSecao, setDadosDebitoIcms] = React.useState<SecaoICMS[]>([]);
    const [dadosCreditoSecao, setDadosCreditoIcms] = React.useState<SecaoICMS[]>([]);


    React.useEffect(() => {
      if (Number(spedFiscalIdSelecionado) > 0){
        const fetchData = async () => {
          try {
            setLoading(true);
            const response = await fetch(process.env.REACT_APP_API_URL+'icms-por-estrutura-mercadologica/'+spedFiscalIdSelecionado);
            const data = await response.json();
      
            // Aqui você pode validar se os dados têm a estrutura esperada
            if (Array.isArray(data)) {
              setDadosEstruturaMercadologicaIcms(data);
            } else {
              console.error('Dados de produtos inválidos:', data);
            }

            const responseSt = await fetch(process.env.REACT_APP_API_URL+'icms-por-estrutura-mercadologica/'+spedFiscalIdSelecionado+'?campoValor=vl_icms_st');
            const dataSt = await responseSt.json();
      
            // Aqui você pode validar se os dados têm a estrutura esperada
            if (Array.isArray(dataSt)) {
              setDadosEstruturaMercadologicaIcmsSt(dataSt);
            } else {
              console.error('Dados de produtos inválidos:', dataSt);
            }

            const responseGrafico1 = await fetch(process.env.REACT_APP_API_URL + 'top10-debito-icms-estrutura/' + spedFiscalIdSelecionado);
            const data1: DebitoIcmsEstruturaMercadologicaSecaoGrafico[] = await responseGrafico1.json();

            if (Array.isArray(data1)) {
              const formatado = data1.map(item => ({
                descricao_secao: item.descricao_secao ?? 'Sem descrição',
                total: item.total
              }));

              setDadosDebitoIcms(formatado);
            }

            const responseGrafico2 = await fetch(process.env.REACT_APP_API_URL+'top10-credito-icms-estrutura/'+spedFiscalIdSelecionado);
            const data2: DebitoIcmsEstruturaMercadologicaSecaoGrafico[] = await responseGrafico2.json();

            if (Array.isArray(data2)) {
              // transforma os dados no formato esperado pelo gráfico de barras horizontais
              const formatado = data2.map(item => ({
                descricao_secao: item.descricao_secao ?? 'Sem descrição',
                total: item.total
              }));

              setDadosCreditoIcms(formatado);
            }

          } catch (e) {
            console.error('Erro ao carregar dados de produtos ICMS', e);
          } finally {
            setLoading(false);
          }
        };
      
        fetchData();
      }
    }, [spedFiscalIdSelecionado]);

    // Função para lidar com clique em seção no gráfico
    const handleSecaoClick = React.useCallback(async (secao: any) => {
        if (spedFiscalIdSelecionado && secao.descricao_secao) {
            await openSecaoModal(
                { nome: secao.descricao_secao, total: secao.total },
                spedFiscalIdSelecionado
            );
        }
    }, [spedFiscalIdSelecionado, openSecaoModal]);

    return (
        <>
            <Grid container spacing={0.5}>
              <SpedStatusHandler
                  onSpedFiscalIdChange={(id) => {
                      setSpedFiscalIdSelecionado(id); // exemplo de uso
                  }}
              />

              <InfoCard title="Análise de ICMS por Seção da Estrutura Mercadológica" icon={<BalanceIcon />} iconColor="info" size={12} />

              <Grid item xs={12} md={6} lg={6}>
                <DonutChartWithTable
                  data={dadosDebitoSecao}
                  title="TOP 10 DÉBITOS DE ICMS POR SEÇÃO"
                  loading={loading}
                  nameKey="descricao_secao"
                  valueKey="total"
                  onItemClick={handleSecaoClick}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <DonutChartWithTable
                  data={dadosCreditoSecao}
                  title="TOP 10 CRÉDITOS DE ICMS POR SEÇÃO"
                  loading={loading}
                  nameKey="descricao_secao"
                  valueKey="total"
                  onItemClick={handleSecaoClick}
                />
              </Grid>

              <Grid item xs={12} md={12} lg={12}>
                  <TableIcmsEstruturaMercadologica data={dadosEstruturaMercadologicaIcms} title="Detalhamento de Créditos e Débitos por Estrutura Mercadológica – ICMS" loading={loading} />
              </Grid>

              <Grid item xs={12} md={12} lg={12}>
                  <TableIcmsEstruturaMercadologica data={dadosEstruturaMercadologicaIcmsSt} title="Detalhamento de Créditos e Débitos por Estrutura Mercadológica – ICMS ST" loading={loading} />
              </Grid>

            </Grid>

            {/* Modal de drilldown de seções */}
            <SecaoDebitosIcmsDrillDownModal
                isOpen={isSecaoModalOpen}
                onClose={closeSecaoModal}
                data={secaoDrillDownData}
                loading={secaoDrillDownLoading}
                selectedSecao={selectedSecao}
                error={secaoDrillDownError}
                onRetry={retrySecaoModal}
            />
        </>
      );

}


export default AnalisesEstruturaMercadologica;

